import type { ReactNode } from 'react';
import { isEndpointMissing, isForbiddenError } from '@/services/http';
import { ButtonLink } from './Button';
import { Panel } from './Panel';

/** Neutral placeholder for an empty list. */
export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="grid place-items-center gap-2 rounded-control border border-dashed border-border px-4 py-10 text-center">
      <p className="font-mono text-[11px] text-content-muted">{message}</p>
      {action}
    </div>
  );
}

/** Shown while a route's data is in flight. */
export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid place-items-center gap-3 px-4 py-16" role="status" aria-live="polite">
      <span className="size-6 animate-spin rounded-full border-2 border-border border-t-primary" />
      <span className="font-mono text-[11px] text-content-muted">{label}</span>
    </div>
  );
}

/**
 * Shown when a detail screen's id matches nothing — a stale or mistyped link.
 *
 * Unlike `ErrorState` there is nothing to retry, so it offers the way back to
 * the list the id should have come from.
 */
export function NotFoundState({
  title,
  message,
  backTo,
  backLabel,
}: {
  title: string;
  message: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <Panel>
      <h2 className="mb-1 text-[15px] font-bold">{title}</h2>
      <p className="mb-4 font-mono text-[11px] text-content-muted">{message}</p>
      <ButtonLink to={backTo} variant="primary">
        {backLabel}
      </ButtonLink>
    </Panel>
  );
}

/** Shown when a service call fails. */
export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  // The server answering "no such route" is a deployment gap, not a failure:
  // nothing is broken and retrying will not help, so it is said calmly.
  // A refusal for this account's role: nothing is broken and retrying will
  // not help, so it reads like the console's own permission screen.
  if (isForbiddenError(error)) {
    return (
      <Panel className="mx-auto mt-10 max-w-md text-center">
        <h2 className="mb-2 text-xl font-extrabold tracking-[-0.5px]">Not available to your role</h2>
        <p className="font-mono text-[11px] text-content-muted">
          The server doesn&apos;t allow this for your account. Ask a super admin if you need
          access.
        </p>
      </Panel>
    );
  }

  if (isEndpointMissing(error)) {
    return (
      <Panel>
        <h2 className="mb-1 text-[15px] font-bold">Not available yet</h2>
        <p className="font-mono text-[11px] text-content-muted">{error.message}</p>
      </Panel>
    );
  }

  return (
    <Panel className="border-danger/30">
      <h2 className="mb-1 text-[15px] font-bold text-danger">Something went wrong</h2>
      <p className="font-mono text-[11px] text-content-muted">{error.message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 text-[11px] font-extrabold uppercase tracking-[0.04em] text-accent hover:opacity-80"
        >
          Try again
        </button>
      ) : null}
    </Panel>
  );
}
